/**
 * D-119 — Shared project-week math.
 *
 * The PM defines a week as **Monday–Sunday**, anchored to the calendar
 * Monday of the week containing the project start date. Any day from
 * Monday through Sunday of a given calendar week counts as that week.
 *
 * Why this anchoring (vs. "every 7 days from project start"):
 *   - C: "But its Monday-Sunday, anything fromn Mon-Friday counts as a
 *      full 1 buisness week."
 *   - Clients + supers think in calendar weeks ("we finished framing
 *      week 6"), not in arbitrary 7-day buckets from project start.
 *   - Pre-D-119 the math was `floor(daysSinceStart / 7) + 1`. For
 *      projects that START ON A MONDAY the two approaches give identical
 *      results; for any other start day the calendar-week approach
 *      better matches PM/client mental model.
 *
 * Both Notes-page Client-Updates week grouping and ProjectPage phase-row
 * "Wk N → Wk M" range labels share this module so the numbering stays
 * coherent across surfaces.
 *
 * @module lib/projectWeeks
 */

const DAY_MS = 86400000;
const WEEK_MS = 7 * DAY_MS;

/**
 * Return the UTC Date of the Monday of the calendar week containing `date`.
 * Uses UTC throughout so DST transitions don't shift the boundary.
 */
function mondayOfWeek(date: Date): Date {
  // JS getUTCDay: 0=Sun, 1=Mon, ..., 6=Sat. Want to roll back to Monday.
  const day = date.getUTCDay();
  const daysSinceMonday = day === 0 ? 6 : day - 1;
  return new Date(date.getTime() - daysSinceMonday * DAY_MS);
}

/**
 * Compute the project-week number for `eventIso` relative to `projectStartIso`.
 *
 *   - Week 1 = the calendar week (Mon-Sun) containing the project start date.
 *   - Week N = N-th calendar week after Week 1.
 *   - Event before Week 1's Monday → returns 0 ("Pre-project" bucket).
 *   - Returns `null` when either date is missing/invalid.
 *
 * Both inputs are date-like strings — only the YYYY-MM-DD prefix is used,
 * the time component is discarded. Calendar math runs in UTC.
 */
export function projectWeekNumber(
  eventIso: string,
  projectStartIso: string,
): number | null {
  if (!eventIso || !projectStartIso) return null;
  const eventDay = new Date(eventIso.slice(0, 10) + 'T00:00:00Z');
  const startDay = new Date(projectStartIso.slice(0, 10) + 'T00:00:00Z');
  if (Number.isNaN(eventDay.getTime()) || Number.isNaN(startDay.getTime())) {
    return null;
  }
  const week1Monday = mondayOfWeek(startDay);
  const eventMonday = mondayOfWeek(eventDay);
  const diffWeeks = Math.round(
    (eventMonday.getTime() - week1Monday.getTime()) / WEEK_MS,
  );
  if (diffWeeks < 0) return 0;
  return diffWeeks + 1;
}

/**
 * Compute the start + end week numbers for a phase spanning
 * `phaseStartIso` to `phaseEndIso`, anchored to `projectStartIso`.
 *
 * Returns `null` when:
 *   - the project has no start date (no anchor)
 *   - the phase has no target_start_date AND no target_end_date
 *
 * Returns `{ start, end }` even when start === end (single-week phase).
 */
export function projectWeekRange(
  phaseStartIso: string | null | undefined,
  phaseEndIso: string | null | undefined,
  projectStartIso: string | null | undefined,
): { start: number; end: number } | null {
  if (!projectStartIso) return null;
  if (!phaseStartIso && !phaseEndIso) return null;
  const startWk =
    phaseStartIso != null
      ? projectWeekNumber(phaseStartIso, projectStartIso)
      : null;
  const endWk =
    phaseEndIso != null
      ? projectWeekNumber(phaseEndIso, projectStartIso)
      : null;
  if (startWk == null && endWk == null) return null;
  // If only one bound is known, mirror it so the chip can still render.
  return {
    start: startWk ?? endWk!,
    end: endWk ?? startWk!,
  };
}

/**
 * Format the calendar-week date range for display next to the week number,
 * e.g. "Apr 06 – Apr 12". Returns empty string for `weekNum < 1`.
 */
export function formatWeekRange(
  projectStartIso: string,
  weekNum: number,
): string {
  if (weekNum < 1) return '';
  const startDay = new Date(projectStartIso.slice(0, 10) + 'T00:00:00Z');
  if (Number.isNaN(startDay.getTime())) return '';
  const week1Monday = mondayOfWeek(startDay);
  const weekStart = new Date(week1Monday.getTime() + (weekNum - 1) * WEEK_MS);
  const weekEnd = new Date(weekStart.getTime() + 6 * DAY_MS);
  const fmt = (d: Date) =>
    d.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      timeZone: 'UTC',
    });
  return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
}
